import Dexie, { type EntityTable } from "dexie";

export type RuntimeMetaRecord = {
  key: string;
  scopeKey: string;
  value: unknown;
  updatedAt: string;
};

export type ProjectionRecord = {
  key: string;
  scopeKey: string;
  domain: string;
  revision: string;
  payload: unknown;
  updatedAt: string;
};

export type JournalRecord = {
  localId: string;
  scopeKey: string;
  kind: string;
  state: string;
  requestKey: string;
  snapshot: unknown;
  createdAt: string;
  updatedAt: string;
};

export type OutboxRecord = {
  requestKey: string;
  scopeKey: string;
  operation: string;
  payload: unknown;
  state: "PENDING" | "SENDING" | "ACKED" | "FAILED";
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CatalogLookupRecord = {
  key: string;
  scopeKey: string;
  menuItemId: string;
  menuVariantId: string | null;
  itemCode: string;
  variantCode: string | null;
  normalizedName: string;
  categoryCode: string | null;
  searchTokens: string[];
  revision: number;
  updatedAt: string;
};

export type CatalogMediaRecord = {
  key: string;
  scopeKey: string;
  mediaAssetId: string;
  contentRevision: string;
  mimeType: string;
  byteSize: number;
  blob: Blob;
  updatedAt: string;
};

export type MemberRuntimeRecord = {
  key: string;
  scopeKey: string;
  customerId: string;
  membershipId: string;
  customerCode: string;
  customerName: string;
  normalizedName: string;
  phone: string | null;
  email: string | null;
  membershipStatus: string;
  joinedAt: string | null;
  searchTokens: string[];
  changedAt: string;
  payload: unknown;
};

export type MemberIdentifierRecord = {
  key: string;
  scopeKey: string;
  customerId: string;
  membershipId: string;
  identifierType: string;
  normalizedValue: string;
  phoneLast4?: string | null;
  displayValue: string | null;
  isPrimary: boolean;
  changedAt: string;
};

export type CustomerCreditRuntimeRecord = {
  key: string;
  scopeKey: string;
  customerId: string;
  creditAccountId: string;
  status: string;
  eligible: boolean;
  branchEligible: boolean;
  creditLimit: number | null;
  currencyCode: string;
  paymentTermDays: number | null;
  versionNo: number;
  changedAt: string;
  payload: unknown;
};

export type LoyaltyRuntimeRecord = {
  key: string;
  scopeKey: string;
  customerId: string;
  projectionKind: string;
  revision: string;
  updatedAt: string;
  payload: unknown;
};

export type StockRuntimeRecord = {
  key: string;
  scopeKey: string;
  stockItemId: string;
  itemCode: string;
  skuCode: string | null;
  normalizedName: string;
  categoryCode: string | null;
  active: boolean;
  quantityOnHand: number | null;
  stateVersion: number | null;
  searchTokens: string[];
  changedAt: string;
  payload: unknown;
};

export type StockIdentifierRecord = {
  key: string;
  scopeKey: string;
  stockItemId: string;
  identifierType: string;
  normalizedValue: string;
  changedAt: string;
};

export type StockMovementRuntimeRecord = {
  key: string;
  scopeKey: string;
  movementId: string;
  stockItemId: string;
  occurredAt: string;
  changedAt: string;
  payload: unknown;
};

export type EmployeeDirectoryRecord = {
  key: string;
  scopeKey: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  normalizedName: string;
  employmentStatus: string;
  eligible: boolean;
  searchTokens: string[];
  changedAt: string;
};

export type ReceivingQueueRecord = {
  key: string;
  scopeKey: string;
  receiptId: string;
  status: string;
  searchTokens: string[];
  updatedAt: string;
  payload: unknown;
};

export type SalesHistoryRecord = {
  key: string;
  scopeKey: string;
  orderId: string;
  businessDate: string;
  status: string;
  orderedAt: string | null;
  searchTokens: string[];
  updatedAt: string;
  payload: unknown;
};

export type SalesOrderDetailRecord = {
  key: string;
  scopeKey: string;
  orderId: string;
  updatedAt: string;
  payload: unknown;
};

export type ShiftRuntimeRecord = {
  key: string;
  scopeKey: string;
  updatedAt: string;
  payload: unknown;
};

class PosRuntimeDb extends Dexie {
  runtimeMeta!: EntityTable<RuntimeMetaRecord, "key">;
  projections!: EntityTable<ProjectionRecord, "key">;
  journal!: EntityTable<JournalRecord, "localId">;
  outbox!: EntityTable<OutboxRecord, "requestKey">;
  catalogLookup!: EntityTable<CatalogLookupRecord, "key">;
  catalogMedia!: EntityTable<CatalogMediaRecord, "key">;
  members!: EntityTable<MemberRuntimeRecord, "key">;
  memberIdentifiers!: EntityTable<MemberIdentifierRecord, "key">;
  customerCredits!: EntityTable<CustomerCreditRuntimeRecord, "key">;
  loyaltyRuntime!: EntityTable<LoyaltyRuntimeRecord, "key">;
  stockRuntime!: EntityTable<StockRuntimeRecord, "key">;
  stockIdentifiers!: EntityTable<StockIdentifierRecord, "key">;
  stockMovementRuntime!: EntityTable<StockMovementRuntimeRecord, "key">;
  employeeDirectory!: EntityTable<EmployeeDirectoryRecord, "key">;
  receivingQueue!: EntityTable<ReceivingQueueRecord, "key">;
  salesHistory!: EntityTable<SalesHistoryRecord, "key">;
  salesOrderDetails!: EntityTable<SalesOrderDetailRecord, "key">;
  shiftRuntime!: EntityTable<ShiftRuntimeRecord, "key">;

  constructor() {
    super("more-pos-runtime-v1");

    this.version(1).stores({
      runtimeMeta: "key, scopeKey, updatedAt",
      projections: "key, scopeKey, domain, revision, updatedAt",
      journal: "localId, scopeKey, kind, state, requestKey, createdAt, updatedAt",
      outbox: "requestKey, scopeKey, operation, state, nextAttemptAt, createdAt, updatedAt",
    });

    // v2 adds rebuildable hot-path indexes only. Existing journal/outbox tables are
    // retained verbatim so a browser upgrade can never discard unsynced business work.
    this.version(2).stores({
      runtimeMeta: "key, scopeKey, updatedAt",
      projections: "key, scopeKey, domain, revision, updatedAt",
      journal: "localId, scopeKey, kind, state, requestKey, createdAt, updatedAt",
      outbox: "requestKey, scopeKey, operation, state, nextAttemptAt, createdAt, updatedAt",
      catalogLookup: "key, scopeKey, menuItemId, menuVariantId, itemCode, variantCode, normalizedName, categoryCode, *searchTokens, revision, updatedAt",
      members: "key, scopeKey, customerId, membershipId, customerCode, normalizedName, phone, membershipStatus, *searchTokens, changedAt",
      memberIdentifiers: "key, scopeKey, customerId, membershipId, identifierType, normalizedValue, [scopeKey+normalizedValue], changedAt",
      customerCredits: "key, scopeKey, customerId, creditAccountId, status, eligible, branchEligible, changedAt",
      loyaltyRuntime: "key, scopeKey, customerId, projectionKind, revision, updatedAt",
      stockRuntime: "key, scopeKey, stockItemId, itemCode, skuCode, normalizedName, categoryCode, active, *searchTokens, stateVersion, changedAt",
      stockIdentifiers: "key, scopeKey, stockItemId, identifierType, normalizedValue, [scopeKey+normalizedValue], changedAt",
      employeeDirectory: "key, scopeKey, employeeId, employeeCode, normalizedName, employmentStatus, eligible, *searchTokens, changedAt",
      receivingQueue: "key, scopeKey, receiptId, status, *searchTokens, updatedAt",
      salesHistory: "key, scopeKey, orderId, businessDate, status, orderedAt, *searchTokens, updatedAt",
      salesOrderDetails: "key, scopeKey, orderId, updatedAt",
      shiftRuntime: "key, scopeKey, updatedAt",
    });

    // v3 adds only a rebuildable phone suffix index. It never clears journal/outbox,
    // terminal credentials, sessions, carts, or any transactional local state.
    this.version(3).stores({
      runtimeMeta: "key, scopeKey, updatedAt",
      projections: "key, scopeKey, domain, revision, updatedAt",
      journal: "localId, scopeKey, kind, state, requestKey, createdAt, updatedAt",
      outbox: "requestKey, scopeKey, operation, state, nextAttemptAt, createdAt, updatedAt",
      catalogLookup: "key, scopeKey, menuItemId, menuVariantId, itemCode, variantCode, normalizedName, categoryCode, *searchTokens, revision, updatedAt",
      members: "key, scopeKey, customerId, membershipId, customerCode, normalizedName, phone, membershipStatus, *searchTokens, changedAt",
      memberIdentifiers: "key, scopeKey, customerId, membershipId, identifierType, normalizedValue, phoneLast4, [scopeKey+normalizedValue], [scopeKey+phoneLast4], changedAt",
      customerCredits: "key, scopeKey, customerId, creditAccountId, status, eligible, branchEligible, changedAt",
      loyaltyRuntime: "key, scopeKey, customerId, projectionKind, revision, updatedAt",
      stockRuntime: "key, scopeKey, stockItemId, itemCode, skuCode, normalizedName, categoryCode, active, *searchTokens, stateVersion, changedAt",
      stockIdentifiers: "key, scopeKey, stockItemId, identifierType, normalizedValue, [scopeKey+normalizedValue], changedAt",
      employeeDirectory: "key, scopeKey, employeeId, employeeCode, normalizedName, employmentStatus, eligible, *searchTokens, changedAt",
      receivingQueue: "key, scopeKey, receiptId, status, *searchTokens, updatedAt",
      salesHistory: "key, scopeKey, orderId, businessDate, status, orderedAt, *searchTokens, updatedAt",
      salesOrderDetails: "key, scopeKey, orderId, updatedAt",
      shiftRuntime: "key, scopeKey, updatedAt",
    }).upgrade(async (transaction) => {
      await transaction.table("memberIdentifiers").toCollection().modify((row: MemberIdentifierRecord) => {
        if (row.identifierType !== "PHONE") {
          row.phoneLast4 = null;
          return;
        }
        const digits = row.normalizedValue.replace(/\D/g, "");
        row.phoneLast4 = digits.length >= 4 ? digits.slice(-4) : null;
      });
    });

    // v4 adds rebuildable Catalog Product Media binary cache only. Transactional
    // journal/outbox state remains untouched. Media cache is scoped by org/branch/node
    // and stores immutable asset revision blobs for offline card rendering.
    this.version(4).stores({
      runtimeMeta: "key, scopeKey, updatedAt",
      projections: "key, scopeKey, domain, revision, updatedAt",
      journal: "localId, scopeKey, kind, state, requestKey, createdAt, updatedAt",
      outbox: "requestKey, scopeKey, operation, state, nextAttemptAt, createdAt, updatedAt",
      catalogLookup: "key, scopeKey, menuItemId, menuVariantId, itemCode, variantCode, normalizedName, categoryCode, *searchTokens, revision, updatedAt",
      catalogMedia: "key, scopeKey, mediaAssetId, contentRevision, [scopeKey+mediaAssetId], updatedAt",
      members: "key, scopeKey, customerId, membershipId, customerCode, normalizedName, phone, membershipStatus, *searchTokens, changedAt",
      memberIdentifiers: "key, scopeKey, customerId, membershipId, identifierType, normalizedValue, phoneLast4, [scopeKey+normalizedValue], [scopeKey+phoneLast4], changedAt",
      customerCredits: "key, scopeKey, customerId, creditAccountId, status, eligible, branchEligible, changedAt",
      loyaltyRuntime: "key, scopeKey, customerId, projectionKind, revision, updatedAt",
      stockRuntime: "key, scopeKey, stockItemId, itemCode, skuCode, normalizedName, categoryCode, active, *searchTokens, stateVersion, changedAt",
      stockIdentifiers: "key, scopeKey, stockItemId, identifierType, normalizedValue, [scopeKey+normalizedValue], changedAt",
      employeeDirectory: "key, scopeKey, employeeId, employeeCode, normalizedName, employmentStatus, eligible, *searchTokens, changedAt",
      receivingQueue: "key, scopeKey, receiptId, status, *searchTokens, updatedAt",
      salesHistory: "key, scopeKey, orderId, businessDate, status, orderedAt, *searchTokens, updatedAt",
      salesOrderDetails: "key, scopeKey, orderId, updatedAt",
      shiftRuntime: "key, scopeKey, updatedAt",
    });

    // v5 adds only a rebuildable, scoped Stock Movement History cache. It is a read model,
    // never a transaction ledger; journal/outbox and every local commit remain untouched.
    this.version(5).stores({
      runtimeMeta: "key, scopeKey, updatedAt",
      projections: "key, scopeKey, domain, revision, updatedAt",
      journal: "localId, scopeKey, kind, state, requestKey, createdAt, updatedAt",
      outbox: "requestKey, scopeKey, operation, state, nextAttemptAt, createdAt, updatedAt",
      catalogLookup: "key, scopeKey, menuItemId, menuVariantId, itemCode, variantCode, normalizedName, categoryCode, *searchTokens, revision, updatedAt",
      catalogMedia: "key, scopeKey, mediaAssetId, contentRevision, [scopeKey+mediaAssetId], updatedAt",
      members: "key, scopeKey, customerId, membershipId, customerCode, normalizedName, phone, membershipStatus, *searchTokens, changedAt",
      memberIdentifiers: "key, scopeKey, customerId, membershipId, identifierType, normalizedValue, phoneLast4, [scopeKey+normalizedValue], [scopeKey+phoneLast4], changedAt",
      customerCredits: "key, scopeKey, customerId, creditAccountId, status, eligible, branchEligible, changedAt",
      loyaltyRuntime: "key, scopeKey, customerId, projectionKind, revision, updatedAt",
      stockRuntime: "key, scopeKey, stockItemId, itemCode, skuCode, normalizedName, categoryCode, active, *searchTokens, stateVersion, changedAt",
      stockIdentifiers: "key, scopeKey, stockItemId, identifierType, normalizedValue, [scopeKey+normalizedValue], changedAt",
      stockMovementRuntime: "key, scopeKey, movementId, stockItemId, occurredAt, changedAt, [scopeKey+stockItemId], [scopeKey+occurredAt]",
      employeeDirectory: "key, scopeKey, employeeId, employeeCode, normalizedName, employmentStatus, eligible, *searchTokens, changedAt",
      receivingQueue: "key, scopeKey, receiptId, status, *searchTokens, updatedAt",
      salesHistory: "key, scopeKey, orderId, businessDate, status, orderedAt, *searchTokens, updatedAt",
      salesOrderDetails: "key, scopeKey, orderId, updatedAt",
      shiftRuntime: "key, scopeKey, updatedAt",
    });
  }
}

export const posRuntimeDb = new PosRuntimeDb();
